import { Button } from "@jugl-web/ui-components";
import { useCallback } from "react";
import { useToast, useTranslations } from "@jugl-web/utils/hooks";
import useEditorContext from "../useEditorContext";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/checkmark.svg";
import PreviewTool from "../../tools/PreviewTool";
import TransformTool from "../../tools/TransformTool";

type ToolbarSubmitButtonProps = {
  type?: "text" | "icon";
};

const ToolbarSubmitButton = ({ type = "text" }: ToolbarSubmitButtonProps) => {
  const { instance, closeModal, setActiveTool, onEdit, onSave } =
    useEditorContext();
  const { t } = useTranslations();
  const { toast } = useToast();

  const handleSubmit = useCallback(async () => {
    if (!instance?.canvas) throw new Error("Canvas not initialized");

    const dataUrl = instance.canvas.toDataURL({
      format: "png",
      multiplier: 1,
      quality: 1,
    });

    await fetch(dataUrl)
      .then((res) => res.blob())
      .then((blob) => {
        onEdit({ file: new File([blob], "image.png", { type: "image/png" }) });
      })
      .catch(() => {
        toast(
          t({
            id: "imageEditor.error.failedToSaveImage",
            defaultMessage: "Failed to save image",
          }),
          {
            variant: "error",
          }
        );
      });
  }, [instance?.canvas, onEdit, toast, t]);

  return type === "text" ? (
    <Button
      onClick={async () => {
        if (instance?.activeTool instanceof TransformTool) {
          const result = await instance.activeTool.crop();

          if (result) {
            instance.setDimensions(result.imgWidth, result.imgHeight);
          }
        }

        setActiveTool("preview", new PreviewTool(instance?.canvas ?? null));
        handleSubmit();
      }}
    >
      {t({
        id: "common.save",
        defaultMessage: "Save",
      })}
    </Button>
  ) : (
    <button
      type="button"
      className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-none bg-primary-500 transition-colors hover:bg-primary-600"
      onClick={() => {
        onSave();
        closeModal();
      }}
    >
      <CheckmarkIcon />
    </button>
  );
};

export default ToolbarSubmitButton;
